import { useState, useEffect } from 'react';
import { Card, Badge, Modal, SlidePanel } from '@/components';
import { PanelSection, SpecGrid, SpecCell } from '@/components/SlidePanel';
import { api, getSlugFromHostname } from '@/api/client';
import { useAuthStore } from '@/stores/auth';
import { toast } from '@/components';
import styles from './DealManagement.module.css';

interface Contract {
  id: string; contract_type: string; title: string; status: string;
  customer_name?: string; property_id?: string; customer_id?: string;
  deal_id?: string; created_at: string;
}
interface Property { id: string; name: string; address: string; }
interface Customer { id: string; name: string; }

interface DealProgressDoc {
  contractType: string;
  label: string;
  required: boolean;
  contractId?: string;
  status?: string;
  exists: boolean;
}
interface DealProgress {
  total: number;
  requiredTotal: number;
  requiredCompleted: number;
  allRequiredSigned: boolean;
  documents: DealProgressDoc[];
}
interface Deal {
  id: string;
  deal_type: string;
  title: string;
  status: string;
  property_id?: string;
  property_name?: string;
  customer_id?: string;
  customer_name?: string;
  notes?: string;
  closed_at?: string;
  created_at: string;
  created_by?: string;
  updated_at: string;
  contracts?: Contract[];
  excel_documents?: any[];
  progress?: DealProgress;
}

const dealTypeLabel: Record<string, string> = { lease: '賃貸取引', sale: '売買取引', management: '管理委託' };
const dealTypeIcon: Record<string, string> = { lease: '🏠', sale: '🤝', management: '🔑' };
const dealStatusLabel: Record<string, string> = { in_progress: '進行中', completed: '完了', cancelled: '中止' };
const dealStatusVariant: Record<string, 'gray' | 'blue' | 'green' | 'red' | 'amber'> = { in_progress: 'blue', completed: 'green', cancelled: 'red' };
const contractStatusLabel: Record<string, string> = { draft: '作成中', pending: '確認中', sent: '送付済', signed: '署名済', expired: '期限切れ' };
const contractStatusVariant: Record<string, 'gray' | 'blue' | 'green' | 'red' | 'amber'> = { draft: 'gray', pending: 'amber', sent: 'blue', signed: 'green', expired: 'red' };

function ProgressBar({ progress }: { progress: DealProgress }) {
  const pct = progress.requiredTotal > 0 ? Math.round((progress.requiredCompleted / progress.requiredTotal) * 100) : 0;
  return (
    <div className={styles.progressWrap}>
      <div className={styles.progressBar}>
        <div className={styles.progressFill} style={{ width: `${pct}%` }} />
      </div>
      <span className={styles.progressText}>{progress.requiredCompleted}/{progress.requiredTotal}</span>
    </div>
  );
}

// contractType → Excel帳票カテゴリのマッピング
const contractTypeToExcelCategory: Record<string, string> = {
  important_explanation: 'important_explanation',
  lease: 'lease',
  sale: 'sales',
  management: 'management',
  application: 'other',
};

interface Props {
  properties: Property[];
  customers: Customer[];
  onOpenContract?: (contract: Contract) => void;
  onOpenExcel?: (dealId: string, propertyId?: string, customerId?: string, propertyName?: string, customerName?: string, category?: string, contractId?: string) => void;
  onOpenExcelDraft?: (docId: string) => void;
  autoOpenDealId?: string;
  onAutoOpenConsumed?: () => void;
}

export function DealManagement({ properties, customers, onOpenContract, onOpenExcel, onOpenExcelDraft, autoOpenDealId, onAutoOpenConsumed }: Props) {
  const [deals, setDeals] = useState<Deal[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterType, setFilterType] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [selectedDeal, setSelectedDeal] = useState<Deal | null>(null);
  const [detailDeal, setDetailDeal] = useState<Deal | null>(null);

  // 作成フォーム
  const [createForm, setCreateForm] = useState({ deal_type: 'lease', title: '', property_id: '', customer_id: '', customer_name: '', notes: '' });

  const fetchDeals = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filterStatus) params.set('status', filterStatus);
      if (filterType) params.set('deal_type', filterType);
      if (searchQuery) params.set('q', searchQuery);
      const data = await api<Deal[]>(`/tenant/deals?${params}`);
      setDeals(data);
    } catch { toast('取引一覧の取得に失敗しました', 'error'); }
    finally { setLoading(false); }
  };

  const fetchDealDetail = async (id: string) => {
    try {
      const data = await api<Deal>(`/tenant/deals/${id}`);
      setDetailDeal(data);
    } catch { toast('取引詳細の取得に失敗しました', 'error'); }
  };

  useEffect(() => { fetchDeals(); }, [filterStatus, filterType, searchQuery]);

  // URLクエリで指定された取引を自動でパネル表示
  useEffect(() => {
    if (!autoOpenDealId || loading || !deals.length) return;
    const target = deals.find(d => d.id === autoOpenDealId);
    if (target) {
      setSelectedDeal(target);
      fetchDealDetail(target.id);
      onAutoOpenConsumed?.();
    }
  }, [autoOpenDealId, loading, deals]);

  const handleCreate = async () => {
    if (!createForm.deal_type) { toast('取引タイプを選択してください', 'error'); return; }
    // 顧客名を自動セット
    let customerName = createForm.customer_name;
    if (!customerName && createForm.customer_id) {
      const cust = customers.find(c => c.id === createForm.customer_id);
      if (cust) customerName = cust.name;
    }
    // タイトル自動生成
    let title = createForm.title;
    if (!title) {
      const prop = properties.find(p => p.id === createForm.property_id);
      title = `${prop?.name || ''} ${customerName || ''} ${dealTypeLabel[createForm.deal_type]}`.trim();
    }
    try {
      await api('/tenant/deals', {
        method: 'POST',
        body: JSON.stringify({
          ...createForm,
          title,
          customer_name: customerName,
        }),
      });
      setShowCreate(false);
      setCreateForm({ deal_type: 'lease', title: '', property_id: '', customer_id: '', customer_name: '', notes: '' });
      fetchDeals();
      toast('取引を作成しました。必須書類のドラフトも自動生成されました。', 'success');
    } catch { toast('取引の作成に失敗しました', 'error'); }
  };

  const handleCancel = async (dealId: string) => {
    try {
      await api(`/tenant/deals/${dealId}`, { method: 'PATCH', body: JSON.stringify({ status: 'cancelled' }) });
      fetchDeals();
      if (detailDeal?.id === dealId) fetchDealDetail(dealId);
      toast('取引を中止しました', 'success');
    } catch { toast('取引の中止に失敗しました', 'error'); }
  };

  const handleDelete = async (dealId: string) => {
    try {
      await api(`/tenant/deals/${dealId}`, { method: 'DELETE' });
      setDetailDeal(null);
      fetchDeals();
      toast('取引を削除しました', 'success');
    } catch { toast('取引の削除に失敗しました', 'error'); }
  };

  return (
    <div>
      {/* フィルタ＆作成ボタン */}
      <div className={styles.toolbar}>
        <div className={styles.filters}>
          <input
            type="text"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder="🔍 物件名・顧客名で検索"
            className={styles.searchInput}
          />
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className={styles.filterSelect}>
            <option value="">全ステータス</option>
            <option value="in_progress">進行中</option>
            <option value="completed">完了</option>
            <option value="cancelled">中止</option>
          </select>
          <select value={filterType} onChange={e => setFilterType(e.target.value)} className={styles.filterSelect}>
            <option value="">全タイプ</option>
            <option value="lease">賃貸取引</option>
            <option value="sale">売買取引</option>
            <option value="management">管理委託</option>
          </select>
        </div>
        <button className={styles.btnPrimary} onClick={() => setShowCreate(true)}>＋ 新規取引</button>
      </div>

      {/* 取引一覧 */}
      {loading ? (
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--ink3)' }}>読み込み中...</div>
      ) : deals.length === 0 ? (
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--ink3)' }}>取引データはありません</div>
      ) : (
        <div className={styles.dealList}>
          {deals.map(deal => (
            <div key={deal.id} className={styles.dealItem} onClick={() => { setSelectedDeal(deal); fetchDealDetail(deal.id); }}>
              <div className={styles.dealIcon}>{dealTypeIcon[deal.deal_type] || '📋'}</div>
              <div className={styles.dealBody}>
                <div className={styles.dealTitle}>{deal.title}</div>
                <div className={styles.dealMeta}>
                  {dealTypeLabel[deal.deal_type]} · {deal.property_name || '物件未選択'}
                  {deal.customer_name && ` · ${deal.customer_name}様`}
                </div>
              </div>
              <div className={styles.dealRight}>
                {deal.progress && <ProgressBar progress={deal.progress} />}
                <div className={styles.dealDate}>{new Date(deal.created_at).toLocaleDateString('ja-JP')}</div>
              </div>
              <Badge variant={dealStatusVariant[deal.status] ?? 'gray'}>{dealStatusLabel[deal.status] ?? deal.status}</Badge>
            </div>
          ))}
        </div>
      )}

      {/* 新規作成モーダル */}
      <Modal open={showCreate} onClose={() => setShowCreate(false)} title="新規取引を作成">
        <div className={styles.form}>
          <div className={styles.formGroup}>
            <label>取引タイプ</label>
            <div className={styles.typeCards}>
              {(['lease', 'sale', 'management'] as const).map(t => (
                <div key={t}
                  className={`${styles.typeCard} ${createForm.deal_type === t ? styles.typeCardActive : ''}`}
                  onClick={() => setCreateForm(f => ({ ...f, deal_type: t }))}
                >
                  <span className={styles.typeCardIcon}>{dealTypeIcon[t]}</span>
                  <span>{dealTypeLabel[t]}</span>
                </div>
              ))}
            </div>
          </div>
          <div className={styles.formGroup}>
            <label>物件</label>
            <select value={createForm.property_id} onChange={e => setCreateForm(f => ({ ...f, property_id: e.target.value }))} className={styles.formInput}>
              <option value="">選択してください</option>
              {properties.map(p => <option key={p.id} value={p.id}>{p.name} ({p.address})</option>)}
            </select>
          </div>
          <div className={styles.formGroup}>
            <label>顧客</label>
            <select value={createForm.customer_id} onChange={e => setCreateForm(f => ({ ...f, customer_id: e.target.value }))} className={styles.formInput}>
              <option value="">選択してください</option>
              {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div className={styles.formGroup}>
            <label>タイトル（空欄で自動生成）</label>
            <input type="text" value={createForm.title} onChange={e => setCreateForm(f => ({ ...f, title: e.target.value }))} className={styles.formInput} placeholder="物件名 顧客名 取引タイプ" />
          </div>
          <div className={styles.formGroup}>
            <label>備考</label>
            <textarea value={createForm.notes} onChange={e => setCreateForm(f => ({ ...f, notes: e.target.value }))} className={styles.formInput} rows={3} />
          </div>
          <div className={styles.formActions}>
            <button className={styles.btnOutline} onClick={() => setShowCreate(false)}>キャンセル</button>
            <button className={styles.btnPrimary} onClick={handleCreate}>作成する</button>
          </div>
          <div className={styles.createNote}>
            作成時に必須書類のドラフトが自動生成されます
          </div>
        </div>
      </Modal>

      {/* 取引詳細パネル */}
      <SlidePanel
        open={!!selectedDeal}
        onClose={() => { setSelectedDeal(null); setDetailDeal(null); }}
        title={selectedDeal ? `${dealTypeIcon[selectedDeal.deal_type]} ${selectedDeal.title}` : ''}
        width={600}
        actions={selectedDeal && detailDeal ? (
          <div style={{ display: 'flex', gap: 6 }}>
            {onOpenExcel && (
              <button className={styles.btnOutline} onClick={() => onOpenExcel(
                detailDeal.id,
                detailDeal.property_id,
                detailDeal.customer_id,
                detailDeal.property_name,
                detailDeal.customer_name,
              )}>📊 書類作成</button>
            )}
            {detailDeal.status === 'in_progress' && (
              <button className={styles.btnDanger} onClick={() => handleCancel(detailDeal.id)}>取引中止</button>
            )}
          </div>
        ) : undefined}
      >
        {detailDeal ? (
          <>
            {/* 基本情報 */}
            <PanelSection title="基本情報">
              <SpecGrid>
                <SpecCell label="取引タイプ" value={dealTypeLabel[detailDeal.deal_type]} />
                <SpecCell label="ステータス" value={dealStatusLabel[detailDeal.status]} />
                <SpecCell label="物件" value={detailDeal.property_name || '未選択'} />
                <SpecCell label="顧客" value={detailDeal.customer_name ? `${detailDeal.customer_name}様` : '未選択'} />
                <SpecCell label="作成日" value={new Date(detailDeal.created_at).toLocaleDateString('ja-JP')} />
                {detailDeal.closed_at && <SpecCell label="完了/中止日" value={new Date(detailDeal.closed_at).toLocaleDateString('ja-JP')} />}
              </SpecGrid>
              {detailDeal.notes && <div className={styles.notes}>{detailDeal.notes}</div>}
            </PanelSection>

            {/* 進捗サマリー */}
            {detailDeal.progress && (
              <PanelSection title="書類進捗">
                <div className={styles.progressSummary}>
                  <ProgressBar progress={detailDeal.progress} />
                  <span className={styles.progressLabel}>
                    必須書類 {detailDeal.progress.requiredCompleted}/{detailDeal.progress.requiredTotal} 完了
                    {detailDeal.progress.allRequiredSigned && ' ✅'}
                  </span>
                </div>
              </PanelSection>
            )}

            {/* 書類チェックリスト */}
            <PanelSection title="書類一覧">
              {detailDeal.progress?.documents.map((doc, i) => {
                const contract = detailDeal.contracts?.find(c => c.id === doc.contractId);
                // 対応するExcel帳票を検索（PDF生成済み=exported を優先、なければ最新の下書き）
                const excelCategory = contractTypeToExcelCategory[doc.contractType];
                const matches = detailDeal.excel_documents?.filter((ed: any) =>
                  ed.template_category === excelCategory || ed.contract_id === doc.contractId
                ) || [];
                const matchingExcelDoc = matches.find((ed: any) => ed.status === 'exported') || matches[0];
                return (
                  <div key={i} className={styles.docRow}
                    onClick={() => contract && onOpenContract?.(contract)}
                    style={{ cursor: contract ? 'pointer' : 'default' }}
                  >
                    <div className={styles.docRowIcon} style={{
                      background: doc.status === 'signed' ? '#ecfdf5' : doc.exists ? '#eff6ff' : '#f9fafb',
                    }}>
                      {doc.status === 'signed' ? '✅' : doc.exists ? '📝' : '⬜'}
                    </div>
                    <div className={styles.docRowInfo}>
                      <div className={styles.docRowMain}>
                        {doc.label}
                        {doc.required && <span className={styles.requiredBadge}>必須</span>}
                        {!doc.required && <span className={styles.optionalBadge}>任意</span>}
                      </div>
                      <div className={styles.docRowSub}>
                        {doc.exists
                          ? contractStatusLabel[doc.status || ''] || doc.status
                          : '未作成'}
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                      {doc.exists && doc.status && (
                        <Badge variant={contractStatusVariant[doc.status] ?? 'gray'}>{contractStatusLabel[doc.status] ?? doc.status}</Badge>
                      )}
                      {matchingExcelDoc && (
                        <button className={styles.btnExcel} onClick={async (e) => {
                          e.stopPropagation();
                          try {
                            const { token, user } = useAuthStore.getState();
                            const headers: Record<string, string> = {};
                            if (token) headers['Authorization'] = `Bearer ${token}`;
                            const slug = getSlugFromHostname() || user?.tenantSlug;
                            if (slug) headers['X-Tenant-Slug'] = slug;
                            const res = await fetch(`/api/tenant/excel-documents/${matchingExcelDoc.id}/download-pdf`, { headers });
                            if (!res.ok) { toast('PDFが生成されていません。先にExcel帳票からPDF生成してください。', 'error'); return; }
                            const blob = await res.blob();
                            const url = URL.createObjectURL(blob);
                            window.open(url, '_blank');
                          } catch { toast('PDF表示に失敗しました', 'error'); }
                        }}>📄 PDF</button>
                      )}
                      {onOpenExcel && contractTypeToExcelCategory[doc.contractType] && doc.contractType !== 'application' && (
                        <button className={styles.btnExcel} onClick={(e) => {
                          e.stopPropagation();
                          onOpenExcel(
                            detailDeal!.id,
                            detailDeal!.property_id,
                            detailDeal!.customer_id,
                            detailDeal!.property_name,
                            detailDeal!.customer_name,
                            contractTypeToExcelCategory[doc.contractType],
                            doc.contractId,
                          );
                        }}>📊 書類作成</button>
                      )}
                    </div>
                  </div>
                );
              })}
            </PanelSection>

            {/* 紐づく契約一覧（書類定義外のもの） */}
            {detailDeal.contracts && detailDeal.contracts.length > (detailDeal.progress?.documents.length || 0) && (
              <PanelSection title="その他の紐づき書類">
                {detailDeal.contracts
                  .filter(c => !detailDeal.progress?.documents.some(d => d.contractId === c.id))
                  .map(c => (
                    <div key={c.id} className={styles.docRow} onClick={() => onOpenContract?.(c)} style={{ cursor: 'pointer' }}>
                      <div className={styles.docRowIcon}>📄</div>
                      <div className={styles.docRowInfo}>
                        <div className={styles.docRowMain}>{c.title}</div>
                        <div className={styles.docRowSub}>{contractStatusLabel[c.status] || c.status}</div>
                      </div>
                      <Badge variant={contractStatusVariant[c.status] ?? 'gray'}>{contractStatusLabel[c.status] ?? c.status}</Badge>
                    </div>
                  ))}
              </PanelSection>
            )}

            {/* Excel帳票出力履歴 */}
            {detailDeal.excel_documents && detailDeal.excel_documents.length > 0 && (
              <PanelSection title="Excel帳票出力履歴">
                {detailDeal.excel_documents.map((doc: any) => {
                  const isDraft = doc.status !== 'exported';
                  return (
                    <div key={doc.id} className={styles.docRow}
                      style={{ cursor: isDraft && onOpenExcelDraft ? 'pointer' : 'default' }}
                      onClick={() => { if (isDraft && onOpenExcelDraft) onOpenExcelDraft(doc.id); }}>
                      <div className={styles.docRowIcon}>📊</div>
                      <div className={styles.docRowInfo}>
                        <div className={styles.docRowMain}>{doc.template_name || 'Excel帳票'}</div>
                        <div className={styles.docRowSub}>{new Date(doc.created_at).toLocaleString('ja-JP', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })}</div>
                      </div>
                      {isDraft && onOpenExcelDraft && (
                        <span style={{ fontSize: 12, color: 'var(--ink3)', marginRight: 4 }} title="編集する">✏️</span>
                      )}
                      <Badge variant={doc.status === 'exported' ? 'green' : 'gray'}>{doc.status === 'exported' ? '出力済' : '下書き'}</Badge>
                    </div>
                  );
                })}
              </PanelSection>
            )}
          </>
        ) : (
          <div style={{ padding: 24, textAlign: 'center', color: 'var(--ink3)' }}>読み込み中...</div>
        )}
      </SlidePanel>
    </div>
  );
}
